import { Languages } from "../../../Utils/Languages";
import { AppContext } from "../../../../Common/AppContext";
import { BetManager } from "../../../Managers/BetManager";
import { DataManager } from "../../../Managers/DataManager";
import { GameData } from "../Datas/GameData";
import { Communication } from "neo-games";
import { ModuleManager } from "../../../Managers/ModuleManager";
import { FreeRoundBonus } from "../Bonuses/FreeRoundBonus";
import { Jackpot } from "../Bonuses/Jackpot";
import { FreeRoundBonusDialog } from "./FreeRoundBonusDialog";

/**
 * Class representing the Bet Higher Than Minimum Dialog.
 */
export class BetHigherThanMinDialog {
    private static _wasOpened: boolean = false;
    private static _isOpened: boolean = false;
    private static _reopenDialog: boolean = false;
    private static _gameStartedWithFeature: boolean = false;
    private static _gameStarted: boolean = true;

    /**
     * Opens the dialog if the conditions are met.
     */
    public static openDialog(): void {
        if (
            !this.canNotifyBetHigherThanMin() ||
            (this._wasOpened && !this._reopenDialog) ||
            FreeRoundBonusDialog.dialogActive()
        ) {
            return;
        }

        const minBet =
            Languages.formatText(Languages.FormatMoney, this.getMinBet().toString()) +
            " " +
            DataManager.getDataById(GameData.DataCurrency);
        const message = Languages.getRid("EXCEED_MIN_BET", minBet);

        const dialogData = {
            text: message,
            actions: [
                {
                    type: "restore",
                    text: Languages.getRid("CONTINUE"),
                    customData: Communication.Game.GameModules.Base,
                },
            ],
            special: true,
        };

        AppContext.current.showDialog(message, false, null, null, null, dialogData);
        this._wasOpened = true;
        this._isOpened = true;
    }

    /**
     * Determines if the dialog can be opened.
     * @returns True if the dialog can be opened, otherwise false.
     */
    public static canOpenDialog(): boolean {
        return (
            AppContext.gameConfiguration.NotifyBetHigherThanMin &&
            !this.isMinBet() &&
            !ModuleManager.gambleActive() &&
            !(FreeRoundBonus.status && FreeRoundBonus.status === "active") &&
            !Jackpot.hasMiniGame() &&
            !this._wasOpened &&
            this._gameStartedWithFeature
        );
    }

    /**
     * Closes the dialog if it is opened or prevents from opening if bet was changed.
     * @param changeBet - Indicates whether the bet was changed.
     */
    public static closeDialog(changeBet?: boolean): void {
        if (this._isOpened) {
            this._isOpened = false;
            AppContext.current.closeDialog();
        }
        if (changeBet) {
            this._wasOpened = true;
        }
    }

    /**
     * Hides the dialog for later restoration.
     */
    public static hideDialog(): void {
        if (this._isOpened) {
            this._reopenDialog = true;
        }
    }

    /**
     * Restores the dialog if it was hidden.
     */
    public static restoreDialog(): void {
        if (this._reopenDialog) {
            this.openDialog();
            this._reopenDialog = false;
        }
    }

    /**
     * Sets the opened status of the dialog to false.
     */
    public static setOpenedStatus(): void {
        if (this._isOpened) {
            this._isOpened = false;
        }
    }

    /**
     * Checks if a game feature is currently active.
     * @returns True if any game feature is active, otherwise false.
     */
    private static gameFeatureActive(): boolean {
        return (
            ModuleManager.gambleActive() ||
            (FreeRoundBonus.status && FreeRoundBonus.status === "active") ||
            Jackpot.hasMiniGame() ||
            !DataManager.GameRoundClosed
        );
    }

    /**
     * Checks if the current bet is the minimum bet.
     * @returns True if the current bet is the minimum bet, otherwise false.
     */
    private static isMinBet(): boolean {
        BetManager.updateBetPosition();
        return BetManager.minBet;
    }

    /**
     * Determines if a notification for a bet higher than the minimum should be sent.
     * @returns True if a notification should be sent, otherwise false.
     */
    public static canNotifyBetHigherThanMin(): boolean {
        return (
            !this.isMinBet() &&
            AppContext.gameConfiguration.NotifyBetHigherThanMin &&
            this._gameStartedWithFeature &&
            !this.gameFeatureActive()
        );
    }

    /**
     * Gets the minimum bet value from total bets.
     * @returns The minimum bet.
     */
    private static getMinBet(): number {
        return Math.min(...BetManager.TotalBets);
    }

    /**
     * Checks if the game started with a feature.
     * @returns True if the game started with a feature active, otherwise false.
     */
    public static getGameStartedWithoutFeature(): boolean {
        if (this._gameStarted) {
            this._gameStartedWithFeature = this.gameFeatureActive();
            this._gameStarted = false;
        }
        return !this._gameStartedWithFeature && AppContext.gameConfiguration.NotifyBetHigherThanMin;
    }
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/Channels/Common/Dialogs/BetHigherThanMinDialog.ts
 **/